import axios from 'axios';
import CustomSelect from '../../components/form/custom-select';
import Observer from '../../common/scripts/observer';
import progressListPanoramasTemplate from './templates/progress-list-panoramas.twig';
import progressListTemplate from './templates/progress-list.twig';
import progressListVideosTemplate from './templates/progress-list-videos.twig';
import ProgressPlayer from '../../components/progress-player';
import {Utils} from '../../common/scripts/utils';

const observer = new Observer();

class Progress {
    constructor() {
        /**
         * Целевой элемент
         */
        this.wrapper = null;

        /**
         * URL для отправки формы
         */
        this.action = null;

        /**
         * Шаблон вывода данных
         */
        this.template = null;

        /**
         * Шаблон вывода данных для видео
         */
        this.videosTemplate = null;

        /**
         * Шаблон вывода данных для панорам
         */
        this.panoramasTemplate = null;

        /**
         * Обёртка для вывода данных/контента
         */
        this.listWrapper = null;

        /**
         * Данные полученные с бэка для вывода на страницу
         */
        this.progressData = null;

        /**
         * Обёртка селектора с датой
         */
        this.selectWrapper = null;

        /**
         * Обёртка для контента
         */
        this.contentWrapper = null;
        this.videosListWrapper = null;
        this.panoramListWrapper = null;

        /**
         * Элементы навигации/переход к следующей/предыдущей дате
         */
        this.navigation = null;
    }

    init(options) {
        this.wrapper = options.wrapper;

        this._getElements();
        this._setTemplate();

        if (this.selectWrapper) {
            this._initCustomSelect();
            this._send();
        }

        this._bindEvents();
        this._observerSubscribe();
    }

    /**
     * Получает элементы для дальнейшей работы
     * @private
     */
    _getElements() {
        this.selectWrapper = this.wrapper.querySelector('.j-progress-select');
        this.action = this.selectWrapper.dataset.action;
        this.listWrapper = this.wrapper.querySelector('.j-progress-content');
        this.contentWrapper = this.wrapper.querySelector('.j-progress-wrapper');
        this.videosListWrapper = this.wrapper.querySelector('.j-progress-videos-content');
        this.panoramListWrapper = this.wrapper.querySelector('.j-progress-panoramas-content');
        this._getNavigation();
    }

    /**
     * Получает элементы навигации
     * @private
     */
    _getNavigation() {
        this.navigation = Array.from(this.wrapper.querySelectorAll('.j-progress-nav'));
    }

    /**
     * Устанавливает шаблон вывода данных
     * @private
     */
    _setTemplate() {
        this.template = progressListTemplate;
        this.videosTemplate = progressListVideosTemplate;
        this.panoramasTemplate = progressListPanoramasTemplate;
    }

    /**
     * Инициализация кастомных селектов
     * @private
     */
    _initCustomSelect() {
        this.select = new CustomSelect({
            elem: this.selectWrapper
        });

        this.select.init();

        this._bindEventsSelect();
    }

    /**
     * Привязка событий к селекту
     * @private
     */
    _bindEvents() {
        const inputs = Array.from(this.selectWrapper.querySelectorAll('input'));

        this.navigation.forEach((nav) => {
            const id = nav.dataset.id;

            nav.addEventListener('click', () => {
                const active = inputs.find((item) => {
                    return item.checked;
                });
                const input = inputs.find((item) => {
                    return item.id === id;
                });

                active.checked = false;
                input.checked = true;

                this.select.update([{
                    elem: this.selectWrapper
                }]);
                this._scrollTop();

                setTimeout(() => {
                    this._send();
                }, 300);
            });
        });
    }

    /**
     * Привязка событий к селекту
     * @private
     */
    _bindEventsSelect() {
        this.selectWrapper.addEventListener('change', () => {
            this._send();
        });
    }

    /**
     * Метод отправляет данные на сервер.
     * @private
     */
    async _send() {
        this.formData = this._collectData();

        this._toggleLoading();

        await axios.post(this.action, this.formData)
            .then((response) => {
                const success = response.data.success;

                if (success) {
                    this.progressData = response.data.data;

                    this._setTemplateData();
                } else {
                    throw new Error('Ошибка на сервере');
                }
            })
            .catch((err) => {
                console.error(err);
            })
            .finally(() => {
                this._toggleLoading();
            });
    }

    /**
     * Прокручивает к началу страницы
     * @private
     */
    _scrollTop() {
        window.scrollTo({
            top     : 0,
            behavior: 'smooth'
        });
    }

    /**
     * Собирает данные для отправки
     * @return {FormData} - formData
     * @private
     */
    _collectData() {
        const formData = new FormData();
        const options = this.select.getOptions();
        const activeSelect = options.find((item) => {
            return item.checked;
        });

        formData.append('select', activeSelect.value);

        return formData;
    }

    /**
     * Устанавливает данные в шаблон
     * @private
     */
    // eslint-disable-next-line max-statements
    _setTemplateData() {
        const {list, videoList, panoramList, prev, next} = this.progressData;
        const navigation = {
            prev,
            next};

        const renderList = (wrapper, templateFn, dataList) => {
            if (!wrapper) {
                return false;
            }

            const hasContent = dataList && Object.keys(dataList).length > 0;

            Utils.clearHtml(wrapper);

            if (hasContent) {
                Utils.clearHtml(wrapper);
                Utils.insetContent(wrapper, templateFn({
                    list: dataList,
                    navigation
                }));
            }

            this._checkTabContent(wrapper);
            this._observerPublish(wrapper);

            return hasContent;
        };

        // Рендер фото
        renderList(this.listWrapper, this.template, list);

        // Рендер видео
        const hasVideos = renderList(this.videosListWrapper, this.videosTemplate, videoList);

        if (hasVideos) {
            this._initProgressPlayers();
        }

        // Рендер панорамы
        renderList(this.panoramListWrapper, this.panoramasTemplate, panoramList);

        this._getNavigation();
        this._bindEvents();
        this._hiddenOneTabBtn();
    }

    /**
     * Инициализирует видеоплееры для всех видео контейнеров
     * @private
     */
    _initProgressPlayers() {
        const videoContainers = document.querySelectorAll('.video-container');

        if (videoContainers.length === 0) {
            return;
        }

        videoContainers.forEach((container) => {
            try {
                // eslint-disable-next-line no-new
                new ProgressPlayer(container);
            } catch (error) {
                console.error('Ошибка при инициализации видеоплеера для контейнера:', container, error);
            }
        });
    }

    /**
     * Проверяет наличие контента внутри wrapper и показывает соответствующую вкладку
     * @param {HTMLElement} wrapper - DOM-элемент, содержащий контент для проверки
     * @private
     */
    _checkTabContent(wrapper) {
        if (!wrapper) {
            return;
        }

        const parentTabElement = wrapper.closest('[data-tab]');

        if (!parentTabElement) {
            return;
        }

        const tabId = parentTabElement.dataset.tab;

        if (!tabId) {
            return;
        }

        const tabButton = document.querySelector(`.tabs-nav__item-button[data-tab="${tabId}"]`);

        if (!tabButton) {
            return;
        }

        const hasContent = wrapper.children.length > 0 && wrapper.textContent.trim() !== '';

        if (hasContent) {
            tabButton.classList.add('visible');
        } else {
            tabButton.classList.remove('visible');
        }
    }


    /**
     * Скрываем единственную кнопку таба
     * @private
     */
    _hiddenOneTabBtn() {
        const tabsNav = this.wrapper?.querySelector('.progress__tabs-nav');

        if (!tabsNav) {
            return;
        }

        const visibleButtons = tabsNav.querySelectorAll('.tabs-nav__item-button.visible');

        if (visibleButtons.length !== 1) {
            return;
        }

        const [button] = visibleButtons;

        button.classList.remove('visible');
        tabsNav.classList.add('is-empty');
    }

    /**
     * Подписываемся на события
     * @private
     */
    _observerSubscribe() {
        observer.subscribe('popup:open', (popup) => {
            if (!popup.content) {
                return;
            }

            this._observerPublish(popup.content, false);
        });
    }

    /**
     * Обновленией наблюдателей
     * @param {object} container - контейнер где обновляем
     * @param {boolean} newContent - нужно ли обновлять lazyLoad
     * @private
     */
    _observerPublish(container, newContent = true) {
        if (newContent) {
            observer.publish('lazyLoad:refresh', container);
            observer.publish('popup:refresh', container);
        }

        observer.publish('swiper-carousel:refresh', container);
        observer.publish('toggle:refresh', container);
    }

    /**
     * Переключает лоадер вкл/выкл
     * @private
     */
    _toggleLoading() {
        this.contentWrapper.classList.toggle('is-loading');
    }
}

export default Progress;
